import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useServiceAvailability } from '../../../../utils/useServiceAvailability';
import { RequestContext } from '../../../../context/RequestContext'
import { GtagContext } from '../../../../pages/_app'
import { getServiceCategorySettingsByCountryAndRequestType } from '../../../../api/request-category/request-category-api'
import { domesticServiceCalculator } from '../../../../utils/domestic-service-cost-calculator'
import { to12HourFormat } from '../../../../utils/time-converter'
import { AnimatePresence, motion } from 'framer-motion'
import { SystemError } from '../../../error/SystemError'
import { openModal } from '../../../../service/modal-service'
import { LoadingCover } from '../../../commons/LoadingCover'
import { AlertTriangle, CheckCircle, PawPrint } from 'lucide-react'
import { GtagLoader, DateTimeInput, TimeSlotInput, ServiceSummaryCard, NavigationButtons } from '../../../commons/BookingFormComponents'
import { clampServiceDate, isServiceDateOutOfRange, setDateTimeCriteria, toDateStr } from '../../../../utils/service-date'

// Animation variants - simplified to prevent layout shifts
const containerVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            staggerChildren: 0.03,
            delayChildren: 0.05
        }
    }
};

const itemVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.15
        }
    }
};

// Price Warning Badge Component
const PriceWarningBadge = ({ rateAggregatorFactor }) => {
    const warningMessages = {
        late: "Selecting a time during usual business hours may be cheaper",
        emergency: "For a better price, try a weekday at least 2 days in advance",
        urgent: "High demand! A further date may be cheaper",
        sunday: "Next Sunday has high demand! Try a weekday for better price",
        weekend: "Try a weekday for better price",
        ph: "Pricing may be higher on public holidays",
        cl: "We are not operating this day",
        night: "Try booking for slots after 9am for better pricing",
        "early-morning": "Try booking for slots after 9am for better pricing"
    };

    if (!rateAggregatorFactor || !warningMessages[rateAggregatorFactor]) {
        return (
            <motion.div 
                variants={itemVariants}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-xs font-medium"
            >
                <CheckCircle className="w-3.5 h-3.5" />
                Cheapest in your locality!
            </motion.div>
        );
    }

    return (
        <motion.div 
            variants={itemVariants}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                rateAggregatorFactor === 'cl' 
                    ? 'bg-red-50 text-red-700' 
                    : 'bg-amber-50 text-amber-700'
            }`}
        >
            <AlertTriangle className="w-3.5 h-3.5" />
            {warningMessages[rateAggregatorFactor]}
        </motion.div>
    );
};

// Cost Display Card
const CostDisplayCard = ({ servicingCost, countryCurrCd }) => {
    if (servicingCost <= 0) return null;

    return (
        <motion.div 
            variants={itemVariants}
            className="bg-linear-to-br from-orange-50 to-amber-50 rounded-xl p-2 border border-orange-100"
        >
            <div className="text-center space-y-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    Nett Total
                </div>
                <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-bold text-gray-800">{countryCurrCd} {servicingCost}</span>
                </div>
            </div>
        </motion.div>
    );
};

// Frequency Select Component
const FrequencySelect = ({ value, onChange }) => {
    const [isFocused, setIsFocused] = useState(false);
    
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Service Frequency
            </label>
            <div className={`relative rounded-lg overflow-hidden transition-all duration-200 w-fit ${isFocused ? 'shadow-md' : 'shadow-sm'}`}>
                <select 
                    className={`
                        h-11 px-3 py-2.5 text-sm text-orange-600 bg-white rounded-lg border-2 
                        transition-all duration-200 cursor-pointer appearance-none pr-8
                        ${isFocused ? 'border-orange-400' : 'border-gray-100 hover:border-orange-200'}
                        focus:outline-none
                    `}
                    value={value}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    required
                >
                    <option value="Weekly">Weekly</option>
                    <option value="Biweekly">Biweekly</option>
                    <option value="Only Once">Only Once</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

// Pet Checkbox Component
const PetCheckbox = ({ checked, onChange }) => {
    return (
        <motion.div variants={itemVariants}>
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-orange-50 transition-colors group">
                <div className={`
                    relative w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                    ${checked ? 'bg-orange-500 border-orange-500' : 'bg-white border-gray-300 group-hover:border-orange-400'}
                `}>
                    {checked && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                    <input 
                        type="checkbox" 
                        checked={checked} 
                        onChange={onChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <PawPrint className="w-4 h-4 text-orange-500" />
                    <span className="text-sm text-gray-700">I have a pet</span>
                </div>
            </label>
        </motion.div>
    );
};

// Unavailable Service Notice
const UnavailableServiceNotice = ({ message }) => {
    return (
        <motion.div 
            variants={itemVariants}
            className="p-3 bg-red-50 border border-red-100 rounded-lg text-center"
        >
            <div className="text-xs text-red-600">
                {message}
            </div>
        </motion.div>
    );
};

export const DomesticServiceDateTime = (props) => {
    
    const goBack = props.goBack
    const goToSection4 = props.goToSection4    
    const countryName = props.countryName
    const countryCurrCd = props.countryCurrCd
    
    const [ minAllowed, setMinAllowed ] = useState("09:00")
    const [ maxAllowed, setMaxAllowed ] = useState("17:00")
    const [ minDate, setMinDate ] = useState("")
    const [ maxDate, setMaxDate ] = useState("")
    const isInitialLoaded = useRef(false)
    
    const [ displayServiceDate, setDisplayServiceDate ] = useState("")
    const { isGtagLoaded } = useContext(GtagContext)
    const [ serviceHours, setServiceHours ] = useState("")
    const [ serviceType, setServiceType ] = useState("")
    const [ helperType, setHelperType ] = useState("")
    const [ isDayError, setIsDayError ] = useState(false)
    const [ hasPet, setHasPet ] = useState(false)
    const [ serviceFrequency, setServiceFrequency ] = useState("Weekly")

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}) 

    const currentDate = new Date().toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10)

    const { 
        requestCriteria, 
        requestType, 
        serviceDate, 
        setServiceDate, 
        startTime,
        setStartTime,
        endTime,
        setEndTime, 
        setServiceCost,
        serviceSummary, 
        setServiceSummary,
        setTotalDiscountPercent,
        setOriginalCost,
    } = useContext(RequestContext);

    //DOMESTIC COST CALCULATION
    const [ servicingCost, setServicingCost ] = useState(0)
    const [ rateAggregatorFactor, setRateAggregatorFactor ] = useState("")
    //DOMESTIC COST CALCULATION

    const handleServiceDateChange = (e) => {
        // iOS date picker ignores min/max, so clamp the selection into the allowed range
        const selectedDate = clampServiceDate(e.target.value, minDate, maxDate)
        if(!selectedDate) return
        setServiceDate(selectedDate)
        setDisplayServiceDate(new Date(selectedDate).toString().substring(0,16))
        if(selectedDate === currentDate){          
            const min = new Date().getHours() + 1
            setMinAllowed(min + ":00")  
            setMaxAllowed("23:00")        
        } else {
            setMinAllowed("08:00")    
            setMaxAllowed("17:00")
        }
    }

    const handleStartTimeChange = (e) => {
        setStartTime(e.target.value)
        e.target.setCustomValidity("");
    }

    const handleFrequencyChange = (e) =>{
        setServiceFrequency(e.target.value)
        for(let x in requestCriteria){
            if(requestCriteria[x].hasOwnProperty(11)){
                requestCriteria[x][11] = e.target.value
                requestCriteria[x]["criteria"] = "Frequency of service - " + e.target.value
            }
        }
    }

    const handleHasPetChange = (e) => {
        setHasPet(hasPet => !hasPet)
    }

    const submit = (e) => {
        e.preventDefault()

        // Safety net - iOS allows picking a date outside the min/max range
        if(isServiceDateOutOfRange(serviceDate, minDate, maxDate)){
            const clamped = clampServiceDate(serviceDate, minDate, maxDate)
            setServiceDate(clamped)
            setDisplayServiceDate(new Date(clamped).toString().substring(0,16))
            return
        }

        setDateTimeCriteria(requestCriteria,
            {
                'criteria': "Service start date - " + displayServiceDate,
                [99]: serviceDate
            },
            {
                'criteria': "Time slot - " + to12HourFormat(startTime) + " to " + to12HourFormat(endTime),
                [100]: to12HourFormat(startTime) + " to " + to12HourFormat(endTime),
            }
        )
        
        if(servicingCost > 0){
            setServiceCost(servicingCost)
        }    
        
        //gtag form initiate conversion
        try {            
            gtag('event', 'conversion', {'send_to': 'AW-823304067/IxZJCK3lyPgYEIO_yogD'});
        } catch (error) {
            console.log(error)
        }
        
        if(hasPet){
            const summary = serviceSummary + " | I have a pet"
            setServiceSummary(summary)
        }
        
        goToSection4()
    }
    
    const domesticCostCalculatorApi = domesticServiceCalculator(requestCriteria, serviceDate, startTime, endTime, setServicingCost, setRateAggregatorFactor, setServiceSummary, 
        setServiceType, setHelperType, 
        setTotalDiscountPercent,
        setOriginalCost,
        setIsLoading,
        setErrorContent,
        setIsSystemError
    )

    const refresh = useCallback(() => {
        if(isInitialLoaded.current) return
        domesticCostCalculatorApi()
    }, [isInitialLoaded, domesticCostCalculatorApi])

    useEffect(() => {
        if(!requestCriteria || !serviceFrequency) return
        if(!isGtagLoaded) return

        if(!isInitialLoaded.current){
            isInitialLoaded.current = true
            domesticCostCalculatorApi()
        } else {
            domesticCostCalculatorApi()
        }
    }, [requestCriteria, isGtagLoaded, domesticCostCalculatorApi, serviceFrequency])

    useEffect(() => {
        if(!startTime || !serviceHours) return

        const [minHour, minMinutes] = startTime.split(":").map(Number);
        const serviceDuration = parseFloat(serviceHours);
    
        let hour = minHour + Math.floor(serviceDuration);
        let minutes = minMinutes + (serviceDuration % 1) * 60;
    
        if (minutes >= 60) {
            minutes -= 60;
            hour += 1;
        }
    
        if (hour >= 24) {
            hour -= 24;
        }
    
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinutes = minutes.toString().padStart(2, "0");

        setEndTime(`${formattedHour}:${formattedMinutes}`);

    }, [startTime, setEndTime, serviceHours])

    useEffect(() => {
        if(!countryName || !requestType || !serviceHours) return

        const getServiceCategorySettings = async() => {
            try {
                const serviceSetting = await getServiceCategorySettingsByCountryAndRequestType(countryName, requestType)
                
                const minD = new Date()
                
                if(minD.getHours() > 15) {
                    minD.setDate(minD.getDate() + (serviceSetting.nextSlotAdvance + 1))
                } else {
                    minD.setDate(minD.getDate() + serviceSetting.nextSlotAdvance)
                }

                setMinDate(toDateStr(minD))
                
                const sDate = new Date()  
                sDate.setDate(sDate.getDate() + 2)
                if(minD > sDate){
                    // setDate(minD.getDate()) only copies the day of month and can land
                    // before minD when minD rolls over to the next month
                    sDate.setTime(minD.getTime())
                }

                if(sDate.getDay() === 0){
                    sDate.setDate(sDate.getDate() + 1)
                }

                if(sDate.getDay() === 6){
                    sDate.setDate(sDate.getDate() + 2)
                }
                
                setServiceDate(toDateStr(sDate))
                setDisplayServiceDate(sDate.toString().substring(0,16))

                // Bookings are paid when made, so they stay within 60 days: a
                // refund or a reversed SP transfer gets harder the longer the
                // money is held (was 182 days, with a card hold).
                const maxD = new Date()
                maxD.setDate(maxD.getDate() + 60)
                setMaxDate(toDateStr(maxD))                

                setStartTime("09:00")

            } catch (error) {
                console.log(error)
            }
        }
        
        getServiceCategorySettings();

    }, [setServiceDate, setStartTime, countryName, requestType, serviceHours])

    useEffect(() => {
        if(!requestCriteria) return

        let serviceHourFound = false;        

        for(let x in requestCriteria){
            if(requestCriteria[x].hasOwnProperty(11)){
                setServiceFrequency(requestCriteria[x][11])
            }
            if(requestCriteria[x].hasOwnProperty(3)){
                setServiceHours(requestCriteria[x][3])
                serviceHourFound = true
            }
        }

        if(!serviceHourFound){
            setServiceHours(6)
        }
    }, [requestCriteria])

    const showJobScope = () =>{

        if(serviceType === "Part time helper"){
            setErrorContent({'header': "Service Scope", 'body': "Sweeping/vacuuming, dusting, mopping and maintaining general home cleanliness.\n\nBathroom - Bathroom and toilet cleaning.\n\nBedroom - Bed sheet changing.\n\nKitchen - Dishwashing, countertop wiping, hood cleaning & stove cleaning.\n\nWindows - Accessible indoor side of window cleaning.\n\nFan - Wiping of easily accessible standing or table fan. Ceiling fan is out of scope.\n\n* THE HELPER WILL NOT BE CARRYING ANY EQUIPMENT.\n* NOT IN SCOPE - Move-out, Post-renovation or Spring Cleaning.\n* This service is for residential premises only.\n\n"});
            openModal(setIsSystemError)
        } else {
            setErrorContent({'header': "Service Inclusions", 'body': "All Rooms - Wipe all surfaces, including aircon. Clean windows, frames, and sills. Wipe cabinet interiors and shelves if empty. Clean ceiling fans and light fixtures. Vacuum and mop.\n\nKitchen - Clean sinks, dining tables, walls, kitchen appliances, dispose of garbage bags.\n\nBathrooms - Clean toilet bowls, bathtub/shower, glass surfaces, mirrors, basin, tap, ventilation fan, floor, drains, wall tiles, and door. Wipe racks and drawers if empty. Dispose of garbage bags.\n\nBedrooms - Wipe racks and drawers if empty. Dispose of garbage bags.\n\nOther - Wipe glass windows, metal grilles (if any), and clean washing machine exterior."});
            openModal(setIsSystemError)
        }
    }

    // Paused helper types: admin console > Service Availability (utils/useServiceAvailability.js)
    const { getPausedMessage, isSubTypePaused } = useServiceAvailability();
    // serviceType is the deep-clean type ("Spring cleaning", ...) for deep
    // cleans and "Part time helper" for helpers.
    const isServiceUnavailable = isSubTypePaused("Domestic Services", helperType)
        || isSubTypePaused("Domestic Services", serviceType);

    return (
        <>
            <form className="max-w-lg mx-auto px-3 py-4" onSubmit={(e) => submit(e)}>
                {/* Loading State */}
                <div className="min-h-35 space-y-2">
                    <GtagLoader isLoading={!isGtagLoaded} />                    
                    {isGtagLoaded && (
                        <motion.div 
                            className="space-y-2"
                            variants={containerVariants}
                            initial="initial"
                            animate="animate"
                        >
                            {/* Cost Display */}
                            <CostDisplayCard 
                                servicingCost={servicingCost}
                                countryCurrCd={countryCurrCd}
                            />
                            
                            {/* Price Warning */}
                            <motion.div variants={itemVariants} className="text-center">
                                <PriceWarningBadge rateAggregatorFactor={rateAggregatorFactor} />
                            </motion.div>
                        </motion.div>
                    )}
                </div>                        
                {/* Date & Frequency Selection */}
                <motion.div 
                    variants={itemVariants}
                    className="flex items-end gap-4 flex-wrap"
                >
                    {/* Service Start Date */}
                    <DateTimeInput
                        type="date"
                        value={serviceDate}
                        min={minDate}
                        max={maxDate}
                        onChange={handleServiceDateChange}
                        label="Service Date"
                        disabled={!minDate}
                    />
                    
                    {/* Service Frequency - only for Helper type */}
                    {helperType === "Helper" && (
                        <FrequencySelect
                            value={serviceFrequency}
                            onChange={handleFrequencyChange}
                        />
                    )}
                </motion.div>
                
                {/* Service Time Slot */}
                <motion.div variants={itemVariants} className='pt-5'>
                    <TimeSlotInput
                        startTime={startTime}
                        endTime={endTime}
                        onStartTimeChange={handleStartTimeChange}
                        minAllowed={minAllowed}
                        maxAllowed={maxAllowed}
                        variant="service"
                    />
                </motion.div>
                
                {/* Pet Checkbox - only for Part time helper */}
                <div className='min-h-11'>
                    {serviceType === "Part time helper" && (
                        <PetCheckbox 
                            checked={hasPet}
                            onChange={handleHasPetChange}
                        />
                    )}                    
                </div>
                {/* Service Summary */}
                <motion.div variants={itemVariants} className='min-h-24'>
                    <ServiceSummaryCard summary={serviceSummary} />
                </motion.div>
                
                {/* Unavailable Service Notice */}
                {isServiceUnavailable && <UnavailableServiceNotice message={getPausedMessage("Domestic Services")} />}
                
                {/* Navigation Buttons */}
                <NavigationButtons
                    onBack={() => goBack()}
                    showNext={!isServiceUnavailable && rateAggregatorFactor !== "cl"}
                    onNextDisabled={isDayError}
                />
            </form>
            
            <AnimatePresence>
                {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
            </AnimatePresence>
            
            {/* Scripts removed - now loaded globally in _app.js */}
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
            {isLoading && <LoadingCover/>}
        </>
    )
}
